/**
 * English, and the source of truth for keys.
 *
 * Arabic mirrors this file key for key; a test fails the build when the two
 * drift. Keys are grouped by where they appear, with `common` for the words that
 * turn up on every screen - a Save button should not be translated eleven times.
 */
export default {
  common: {
    save: 'Save',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    edit: 'Edit',
    remove: 'Remove',
    delete: 'Delete',
    search: 'Search',
    refresh: 'Refresh',
    retry: 'Retry',
    exportCsv: 'Export CSV',
    exportXlsx: 'Export XLSX',
    download: 'Download',
    upload: 'Upload',
    optional: 'Optional',
    none: 'None',
    never: 'Never',
    any: 'Any',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    loading: 'Loading…',
    from: 'From',
    to: 'To',
    status: 'Status',
    reason: 'Reason',
    note: 'Note',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    segment: 'Segment',
    customer: 'Customer',
    points: 'Points',
    balance: 'Balance',
    discount: 'Discount',
    orderTotal: 'Order total',
    orderReference: 'Order reference',
    channel: 'Channel',
    description: 'Description',
    date: 'Date',
    when: 'When',
    by: 'By',
    type: 'Type',
    value: 'Value',
    code: 'Code',
    reference: 'Reference',
    expires: 'Expires',
    active: 'Active',
    inactive: 'Inactive',
    nobodyYet: 'Nobody yet',
    noPermission: 'You do not have permission to do that.',
    readOnly: 'You have read-only access to these settings.',
    pts: 'pts',
    updatedAt: 'Updated {at}',
  },

  language: {
    label: 'Language',
    switchTo: 'Switch to {name}',
  },

  nav: {
    dashboard: 'Dashboard',
    earn: 'Earn points',
    redeem: 'Redeem',
    refunds: 'Refunds',
    transactions: 'Ledger',
    customers: 'Customers',
    customerDetail: 'Customer',
    segments: 'Segments',
    bulkImports: 'Bulk uploads',
    vouchers: 'Vouchers',
    voucherCodes: 'Voucher codes',
    cashback: 'Cashback',
    campaigns: 'Campaigns',
    reports: 'Reports',
    program: 'Programme',
    notifications: 'Notifications',
    smsProviders: 'SMS gateways',
    smsLog: 'SMS log',
    users: 'Users',
    roles: 'Roles',
    noAccess: 'No access',
    notFound: 'Not found',
    cashier: 'Counter',
    counterReport: 'Counter report',
    counterSettings: 'Counter settings',
    segmentApplications: 'Membership applications',
  },

  groups: {
    Operations: 'Operations',
    Customers: 'Customers',
    Marketing: 'Marketing',
    Insight: 'Insight',
    Configuration: 'Configuration',
  },

  header: {
    closeLabel: 'Close',
    brand: 'Loyalty',
    mainNav: 'Main',
    dismiss: 'Dismiss',
    menu: 'Menu',
    manageAccount: 'Manage account',
    signOut: 'Sign out',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
  },

  status: {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    BLOCKED: 'Blocked',
    LOCKED: 'Locked',
    PENDING: 'Pending',
    PENDING_OTP: 'Pending OTP',
    PENDING_APPROVAL: 'Pending approval',
    PROCESSING: 'Processing',
    COMPLETED: 'Completed',
    COMPLETED_WITH_ERRORS: 'Completed with errors',
    FAILED: 'Failed',
    CONFIRMED: 'Confirmed',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
    EXPIRED: 'Expired',
    SENT: 'Sent',
    AVAILABLE: 'Available',
    ASSIGNED: 'Assigned',
    REDEEMED: 'Redeemed',
    OUT_OF_STOCK: 'Out of stock',
    VERIFIED: 'Verified',
    PENDING_CUSTOMER_APPROVAL: 'Awaiting customer',
    DECLINED: 'Declined',
    REVOKED: 'Revoked',
  },

  transactionType: {
    EARN: 'Earn',
    REDEEM: 'Redeem',
    EXPIRE: 'Expire',
    ADJUST: 'Adjust',
    VOUCHER_REDEEM: 'Voucher',
    CASHBACK: 'Cashback',
    REVERSAL: 'Reversal',
  },

  table: {
    emptyTitle: 'Nothing to show',
    emptyHint: 'Adjust the filters, or come back once there is activity.',
  },

  pagination: {
    range: '{first}–{last} of {total}',
    previous: 'Previous',
    next: 'Next',
    page: 'Page {page} of {pages}',
  },

  fileDrop: {
    prompt: 'Drop a file here, or click to choose',
    accepts: 'Accepts {formats}',
  },

  lookup: {
    placeholder: 'Customer phone number',
    find: 'Find',
    clear: 'Clear',
    notFound: 'No customer is enrolled with that number.',
    failed: 'The lookup failed.',
  },

  errors: {
    generic: 'Something went wrong.',
    network: 'The service could not be reached. Check your connection and try again.',
    forbidden: 'You do not have permission to do that.',
    notFound: 'That could not be found.',
    conflict: 'That conflicts with something already saved.',
    tooLarge: 'That file is too large.',
    unprocessable: 'That cannot be done right now.',
    upstream: 'An upstream service is unavailable.',
    server: 'Something went wrong on the server.',
    unknown: 'The request could not be completed.',
    sessionExpired: 'Your session has expired. Signing you in again.',
  },

  dashboard: {
    fullReport: 'Full report',
    createSegment: 'Create a segment',
    newThisMonth: 'New this month',
    newThisMonthHint: 'Enrolled in the last 30 days',
    outstandingPointsHint: 'What the programme owes',
    expiringSoonHint: 'A win-back opportunity',
    earnedLast7: 'Earned, last 7 days',
    redeemedLast7: 'Redeemed, last 7 days',
    discountGrantedLabel: 'Discount granted',
    smsDelivery: 'SMS delivery',
    pointsInAndOut: 'Points in and out',
    noActivity: 'No activity yet.',
    chartLabel: 'Points earned and redeemed per day over the last 14 days',
    createSegmentHint: 'Create a segment to set earn and redeem rates.',
    title: 'Dashboard',
    subtitle: 'The programme at a glance.',
    totalCustomers: 'Customers',
    activeCustomers: 'Active customers',
    newCustomers: 'New in 30 days',
    outstandingPoints: 'Outstanding points',
    pointsEarned: 'Earned in 7 days',
    pointsRedeemed: 'Redeemed in 7 days',
    discountGranted: 'Discount in 7 days',
    expiringSoon: 'Expiring in 30 days',
    trend: 'Points movement',
    trendHint: 'Earned against redeemed, day by day.',
    earned: 'Earned',
    redeemed: 'Redeemed',
    liability: 'Liability by segment',
    liabilityHint: 'Unspent points held in each segment.',
    seeReports: 'See the full reports',
    manageSegments: 'Manage segments',
    noSegments: 'No segments yet',
    welcome: 'Welcome back, {name}.',
    welcomeAnonymous: 'Welcome back.',
    last14Days: 'Last 14 days',
    segmentMeta: '{customers} customers · {spend} lifetime spend',
  },

  earn: {
    noPermission: 'You do not have permission to award points.',
    title: 'Earn points',
    subtitle: "Award points for an order. The rate comes from the customer's segment.",
    findCustomer: 'Find the customer',
    theOrder: 'The order',
    orderTotalHint: 'What the customer spent.',
    orderReferenceHint: 'Your POS or invoice number. Recorded on the ledger entry.',
    channelHint: 'DINE_IN, DELIVERY and so on. A channel rule can change the rate.',
    award: 'Award points',
    estimate: 'Estimate',
    estimateUnit: 'points',
    estimateHint: "At the programme rate of {rate} points per unit. The customer's segment rate and any channel multiplier are applied by the service, so the figure awarded may differ.",
    belowMinimum: 'This order is below the {minimum} minimum for earning, so it will award nothing. The order is still recorded.',
    lastAward: 'Last award',
    pointsAwarded: 'Points awarded',
    balanceBefore: 'Balance before',
    balanceAfter: 'Balance after',
    awarded: '{points} points awarded',
    awardedDetail: 'Balance is now {balance}.',
    nothingAwarded: 'No points awarded',
    nothingAwardedDetail: 'The order is below the programme minimum for earning.',
    failed: 'Could not award points',
    failedDetail: 'The points could not be awarded.',
    belowThresholdWarning: 'This order is below the {minimum} minimum for earning, so it will award nothing. The order is still recorded.',
  },

  redeem: {
    resendCode: 'Resend the code',
    discountGiven: 'Discount given',
    newBalance: 'New balance',
    verify: 'Verify',
    priceIt: 'Price it',
    useMaximum: 'Use the maximum',
    customerPays: 'Customer pays',
    sendCustomerCode: 'Send the customer a code',
    cannotInitiate: 'You can price a discount but not start one.',
    confirmAndDeduct: 'Confirm and deduct',
    codePlaceholder: '······',
    startOver: 'Start over',
    progress: 'Progress',
    orderTotalHint: 'What the customer would pay before any discount.',
    discountPercent: 'Discount percent',
    discountAmountLabel: 'Discount amount',
    pointsToSpend: 'Points to spend',
    orderAndChannel: 'Order reference and channel',
    orderReferenceHint: 'Your POS or invoice number, recorded on the ledger entry.',
    channelHint: 'DINE_IN, DELIVERY and so on. Affects the rate when a rule exists.',
    whatItCosts: 'What it costs',
    enterOrderTotal: 'Enter an order total to see the discount available.',
    askForCode: 'Ask the customer for their code',
    verificationCode: 'Verification code',
    codeExpiredResend: 'The code has expired — resend it.',
    confirmed: 'Redemption confirmed',
    newCustomerButton: 'New customer',
    title: 'Redeem',
    subtitle: 'Turn points into a discount on an order. The customer confirms with a code.',
    findCustomer: 'Find the customer',
    theOrder: 'The order',
    howMuch: 'How much discount',
    byRate: 'As a percentage',
    byAmount: 'As an amount',
    byPoints: 'As points',
    quote: 'Get a quote',
    quoteTitle: 'The quote',
    discountRate: 'Discount rate',
    discountAmount: 'Discount',
    pointsToDeduct: 'Points deducted',
    netPayable: 'Net payable',
    balanceAfter: 'Balance after',
    withinRange: 'Between {min} and {max} for this customer.',
    notEligible: 'This redemption cannot go ahead',
    sendCode: 'Send the code',
    otpTitle: 'Confirm with the customer',
    otpSent: 'A code was texted to {phone}.',
    otpLabel: 'The code the customer received',
    otpExpires: 'Expires in {time}',
    otpExpired: 'That code has expired. Send another.',
    attemptsLeft: '{count} attempts left',
    resend: 'Send another code',
    resendsLeft: '{count} resends left',
    cancel: 'Cancel this redemption',
    confirm: 'Confirm redemption',
    doneTitle: 'Redeemed',
    doneDetail: '{discount} off. Balance is now {balance}.',
    nextOrder: 'Next order, same customer',
    newCustomer: 'A different customer',
    cancelled: 'Redemption cancelled',
    tierHint: 'This tier allows between {min} and {max} of the order.',
    pointWorth: 'One point is worth {amount}.',
    codeSent: 'A code was sent to {phone}. Nothing has been deducted yet.',
    expiresIn: 'Expires in {time}',
    referenceLabel: 'Reference {reference}',
  },

  refunds: {
    title: 'Refunds',
    subtitle: 'A reversal of an earlier points movement. It needs a manager, and it cannot be approved by whoever raised it.',
    raise: 'Raise a refund',
    raised: 'Raised',
    reverses: 'Reverses',
    anEarn: 'An earn',
    aRedemption: 'A redemption',
    yoursToWithdraw: 'Yours to withdraw',
    withdraw: 'Withdraw',
    approve: 'Approve',
    reject: 'Reject',
    emptyTitle: 'Nothing here',
    emptyHint: 'Refund requests raised by agents appear here for a decision.',
    clearCustomer: 'Clear customer filter',
    approveTitle: 'Approve this refund',
    rejectTitle: 'Reject this refund',
    withdrawTitle: 'Withdraw this request',
    originalEntry: 'Original entry',
    pointsMoved: 'Points moved by the reversal',
    discountReversed: 'Discount reversed',
    raisedBy: 'Raised by',
    approveWarning: 'Approving appends a reversal to the ledger and moves the balance straight away. A clawback may take the balance below zero if the points have already been spent.',
    noteHint: 'Kept with the decision.',
    rejectNeedsReason: 'A rejection needs a reason.',
    approveAndReverse: 'Approve and reverse',
    approved: 'Refund approved',
    approvedDetail: '{points} points reversed. Balance is now {balance}.',
    rejected: 'Refund rejected',
    withdrawn: 'Request withdrawn',
    decisionFailed: 'The decision could not be recorded.',
    raiseSubtitle: 'Find the customer, pick the entry to reverse, and say why.',
    noReversible: 'This customer has no entry that can be reversed. Entries already reversed, and reversals themselves, cannot be refunded again.',
    loadingEntries: 'Loading reversible entries…',
    cannotReverse: 'Cannot be reversed',
    reasonHint: 'Why the order was returned or the redemption voided. A manager reads this.',
    sendForApproval: 'Send for approval',
    requestRaised: 'Refund raised',
    requestRaisedDetail: 'It is waiting for a manager to approve it.',
    requestFailed: 'The request could not be raised.',
    balanceLabel: 'balance {points}',
    noReversibleEntries: 'This customer has no entry that can be reversed. Entries already reversed, and reversals themselves, cannot be refunded again.',
    entryOrder: 'Order {total}',
    entryDiscount: 'discount {amount}',
    entryRef: 'ref {ref}',
    pairBadge: 'Counter entry {reference} — decided together with its pair',
    pairHint: 'This refund and its partner reverse one counter order. Approving or rejecting either decides both.',
    pairDecision: 'This refund is one of a pair for counter entry {reference}. Your decision applies to both: the points spent and the points earned are reversed together, or neither is.',
  },

  transactions: {
    anyType: 'Any type',
    title: 'Ledger',
    subtitle: 'Every points movement, in the order it happened. Entries are never edited — a correction appears as its own reversal.',
    balanceAfter: 'Balance after',
    order: 'Order',
    emptyTitle: 'No movements in this range',
    emptyHint: 'Widen the dates, or clear the type and reference filters.',
    entryTitle: 'Ledger entry',
    entryNumber: 'Entry {id}',
    balanceBefore: 'Balance before',
    linkedTo: 'Linked to',
    performedBy: 'Performed by',
    system: 'System',
    openCustomer: 'Open customer',
    exportFailed: 'The export failed',
  },

  customers: {
    searchLabel: 'Search customers',
    title: 'Customers',
    subtitle: 'Everyone enrolled in the programme. Points belong to the customer directly.',
    enrol: 'Enrol customer',
    searchPlaceholder: 'Name, phone, email or reference',
    allSegments: 'All segments',
    anyStatus: 'Any status',
    lifetimeSpend: 'Lifetime spend',
    enrolled: 'Enrolled',
    emptyTitle: 'No customers match',
    emptyHint: 'Clear the filters, or enrol the first customer.',
    enrolTitle: 'Enrol a customer',
    enrolSubtitle: 'A phone number is all that is required; everything else can follow.',
    firstName: 'First name',
    lastName: 'Last name',
    phoneHint: 'Used for the redemption OTP, so it must be reachable.',
    dateOfBirth: 'Date of birth',
    segmentHint: 'Leave blank to use the programme default.',
    noSegment: 'No segment',
    enrolled_toast: 'Customer enrolled',
    enrolFailed: 'The customer could not be enrolled.',
    loadFailed: 'The customers could not be loaded.',
    enrolButton: 'Enrol',
  },

  customer: {
    adjustPlaceholder: 'e.g. 250 or -250',
    back: '← Customers',
    notFound: 'That customer does not exist.',
    loadFailed: 'The customer could not be loaded.',
    backToList: 'Back to customers',
    enrolledOn: 'enrolled {date}',
    pointsBalance: 'Points balance',
    lifetimeEarned: 'Lifetime earned',
    lifetimeRedeemed: 'Lifetime redeemed',
    expiringIn30: 'Expiring in 30 days',
    nextOn: 'Next on {date}',
    profile: 'Profile',
    job: 'Job',
    address: 'Address',
    externalRef: 'External reference',
    externalRefHint: "Your own system's id for this customer, if there is one.",
    lastActivity: 'Last activity',
    quickActions: 'Quick actions',
    quickActionsHint: 'Serving this customer at the till or on a call.',
    awardPoints: 'Award points for an order',
    redeemAgainst: 'Redeem against an order',
    raiseRefund: 'Raise a refund',
    tabLedger: 'Ledger',
    tabBuckets: 'Expiry tranches',
    tabSpend: 'Spend',
    ledgerEmpty: 'No points movements yet',
    ledgerEmptyHint: 'Every earn, redemption, adjustment and reversal appears here.',
    bucketsHint: 'Points are spent closest-to-expiry first, so the top tranche goes first.',
    bucketsEmpty: 'No unspent tranches',
    bucketsEmptyHint: 'Nothing is waiting to expire.',
    earnedAt: 'Earned',
    earnedPoints: 'Earned points',
    remaining: 'Remaining',
    spendEmpty: 'No spend recorded',
    spendEmptyHint: 'Orders appear here once points are earned against them.',
    orders: '{count} orders',
    editTitle: 'Edit customer',
    saved: 'Customer updated',
    saveFailed: 'The changes could not be saved.',
    segmentTitle: 'Change segment',
    segmentSubtitle: 'The segment sets the earn rate, redeem rate and the discount ceiling.',
    chooseSegment: 'Choose a segment',
    segmentReasonHint: 'Recorded against the change.',
    changeSegment: 'Change segment',
    segmentChanged: 'Segment changed',
    segmentChangedDetail: 'Now in {segment}.',
    segmentFailed: 'Could not change the segment',
    statusTitle: 'Change status',
    statusHint: 'A blocked customer can neither earn nor redeem.',
    apply: 'Apply',
    statusChanged: 'Customer is now {status}',
    statusFailed: 'Could not change the status',
    adjustTitle: 'Adjust points',
    adjustSubtitle: 'A correction, posted to the ledger like any other movement.',
    adjustPointsHint: 'Positive to add, negative to deduct.',
    adjustReasonHint: 'Shown on the ledger entry and in the audit reports.',
    adjustExpiry: 'Expires in (days)',
    adjustExpiryHint: "Leave blank to use the programme's expiry.",
    adjustPreview: 'Balance is {balance}. After this adjustment it would be {after}.',
    postAdjustment: 'Post adjustment',
    adjustAdded: 'Added {points} points',
    adjustDeducted: 'Deducted {points} points',
    adjustFailed: 'The adjustment failed',
    orderCount: '{count} orders',
    membership: 'Membership',
    memberSince: 'Member since',
    viaApplication: 'Via application',
    latestApplication: 'Latest application',
    revokeMembership: 'Revoke membership',
    revokeTitle: 'Revoke this membership',
    revokeWarning: 'The customer goes back to the segment they were in before approval, loses the member discount from their next order, and is notified.',
    revokeHint: 'Kept with the membership and shown to the customer.',
    membershipRevoked: 'Membership revoked',
    membershipRevokedDetail: 'The customer is back in their previous segment.',
  },

  segments: {
    maxDiscountLabel: 'Max discount %',
    title: 'Segments',
    subtitle: 'Each segment sets its own earn rate, redeem rate and the largest discount a customer in it may take on an order.',
    create: 'New segment',
    earnRate: 'Earn rate',
    redeemRate: 'Redeem rate',
    maxDiscount: 'Max discount',
    orderValue: 'Order value',
    customersCount: 'Customers',
    anyOrder: 'Any',
    andUp: '{amount} and up',
    upTo: 'Up to {amount}',
    range: '{min} – {max}',
    emptyTitle: 'No segments yet',
    emptyHint: 'Create one to set the earn and redeem terms for a group of customers.',
    retire: 'Retire',
    activate: 'Activate',
    createTitle: 'New segment',
    editTitle: 'Edit {title}',
    modalSubtitle: 'Rates are entered as percentages of the order total.',
    titleLabel: 'Title',
    earnRateHint: 'Points per unit spent.',
    redeemRateHint: 'Currency each point is worth back.',
    maxDiscountHint: 'The ceiling for one order. The floor is {floor}%.',
    belowFloor: "A ceiling below the programme's {floor}% floor leaves no discount a customer in this segment could take.",
    minOrderValue: 'Min order value',
    minOrderHint: 'Leave blank for no lower bound.',
    maxOrderValue: 'Max order value',
    displayOrder: 'Display order',
    created: 'Segment created',
    updated: 'Segment updated',
    saveFailed: 'The segment could not be saved.',
    retireTitle: 'Retire this segment?',
    activateTitle: 'Activate this segment?',
    retireMessage: 'Customers stay in {title}, but it can no longer be assigned and its terms stop applying to new activity.',
    activateMessage: '{title} becomes assignable again and its rates apply.',
    retired: 'Segment retired',
    activated: 'Segment activated',
    changeFailed: 'Could not change the segment',
    loadFailed: 'The segments could not be loaded.',
    belowFloorWarning: 'A ceiling below the programme\'s {floor}% floor leaves no discount a customer in this segment could take.',
    memberDiscount: 'Member discount',
    openBadge: 'Open for applications',
    titleArLabel: 'Title in Arabic',
    titleArHint: 'Shown to customers using the app in Arabic. The title above is the English.',
    descriptionArLabel: 'Description in Arabic',
    membershipHeading: 'Membership',
    memberDiscountLabel: 'Member discount %',
    memberDiscountHint: 'Taken off every order first, before offers, promo codes and points. 0 for none.',
    memberDiscountInvalid: 'Enter a percentage between 0 and 100.',
    openForApplications: 'Open for applications',
    openForApplicationsHint: 'Customers can apply from the app to join, with their membership card. Each application is reviewed under Membership applications.',
    openWithoutDiscount: 'This segment is open for applications but has no member discount. Approved members would get only its earn and redeem rates.',
  },

  bulk: {
    title: 'Bulk uploads',
    subtitle: 'Load customers, balances and segment assignments from a spreadsheet. Download the example sheet first — it has the exact columns the service expects.',
    customersTitle: 'Customers',
    customersDescription: 'Enrol a list of customers. An existing phone number updates that customer instead of creating a second one.',
    balancesTitle: 'Balances',
    balancesDescription: 'Adjust balances in bulk. Each row posts a normal ledger entry with your reason on it — nothing is overwritten silently.',
    segmentsTitle: 'Customer segments',
    segmentsDescription: 'Move customers between segments, by segment title or id.',
    exampleSheet: 'Example sheet',
    recentUploads: 'Recent uploads',
    allTypes: 'All types',
    started: 'Started',
    file: 'File',
    rows: 'Rows',
    loaded: 'Loaded',
    rejected: 'Rejected',
    dryRunBadge: 'Dry run',
    emptyTitle: 'No uploads yet',
    emptyHint: 'Files you upload appear here with a row-by-row result.',
    uploadTitle: 'Upload {kind}',
    expectedColumns: 'Expected columns: {columns}',
    dryRun: 'Dry run',
    dryRunHint: 'Validates every row and writes nothing. Run this first.',
    willWrite: 'This will write. Balance rows post real ledger entries, and they cannot be edited away afterwards — only reversed.',
    validate: 'Validate',
    uploadAndApply: 'Upload and apply',
    dryRunStarted: 'Dry run started',
    uploadStarted: 'Upload started',
    progressBelow: 'Progress appears in the list below.',
    uploadResult: '{kind} {reference}: {accepted} accepted, {rejected} rejected of {total}.',
    uploadFailed: 'The file could not be uploaded.',
    errorsTitle: 'Rejected rows',
    row: 'Row',
    column: 'Column',
    cellValue: 'Value',
    why: 'Why',
    exportErrors: 'Export rejected rows',
    templateFailed: 'The template could not be downloaded',
    errorsFailed: 'The rejected rows could not be loaded',
    exportFailed: 'The export failed',
    loadFailed: 'The uploads could not be loaded.',
    writeWarning: 'This will write. Balance rows post real ledger entries, and they cannot be edited away afterwards - only reversed.',
  },

  reports: {
    title: 'Reports',
    subtitle: 'Every report the service publishes. The filters below come from the report itself, and each one exports in full — not just the page on screen.',
    catalogue: 'Catalogue',
    run: 'Run report',
    needs: 'Needs {fields}.',
    rowsGenerated: '{rows} rows · generated {when}',
    noRows: 'No rows',
    noRowsHint: 'Nothing matched those parameters.',
    total: 'Total',
    totalsNote: 'Totals cover the whole result set, not only this page.',
    catalogueFailed: 'The report catalogue could not be loaded.',
    runFailed: 'The report could not be run.',
    exportFailed: 'The export failed',
    anyOption: 'Any',
    allSegments: 'All segments',
    needsParameters: 'Needs {names}.',
    resultMeta: '{rows} rows · generated {at}',
  },

  program: {
    title: 'Programme',
    subtitle: 'The defaults and the outer bounds. Segments set their own rates within these; the discount floor and the OTP rules apply to everyone.',
    updated: 'Updated {when}',
    identity: 'Identity',
    displayName: 'Display name',
    currency: 'Currency',
    currencyHint: 'Shown against every amount in the portal.',
    code: 'Code',
    codeHint: 'Fixed for the life of the programme.',
    earning: 'Earning',
    earningHint: 'Used for customers with no segment, and as the fallback everywhere else.',
    earnRate: 'Earn rate %',
    earnRateHint: 'Points per unit spent.',
    minOrderEarn: 'Minimum order to earn',
    minOrderEarnHint: 'Orders below this are recorded but award nothing.',
    expiryDays: 'Points expire after (days)',
    expiryDaysHint: 'Blank means points never expire. Existing tranches keep the expiry they were given.',
    redeeming: 'Redeeming',
    redeemingHint: "A redemption is a discount on an order. The customer's segment sets the ceiling; the floor here applies to everyone.",
    redeemRate: 'Redeem rate %',
    redeemRateHint: 'What one point is worth back.',
    minOrderRedeem: 'Minimum order to redeem',
    minRedeemPoints: 'Minimum points to redeem',
    minDiscount: 'Minimum discount %',
    minDiscountHint: 'No redemption may be smaller than this, in any segment.',
    maxDiscount: 'Maximum discount %',
    maxDiscountHint: "The ceiling for a customer with no segment. A segment's own ceiling wins.",
    floorAboveCeiling: 'The floor is above the ceiling, which leaves no discount anyone could take.',
    otp: 'One-time codes',
    otpHint: 'Every redemption is confirmed with a code texted to the customer. These settings decide how long they have and how many tries they get.',
    otpLength: 'Code length',
    otpTtl: 'Valid for (seconds)',
    otpAttempts: 'Attempts allowed',
    otpAttemptsHint: 'After this the redemption is locked out.',
    otpResends: 'Resends allowed',
    discard: 'Discard changes',
    saveProgram: 'Save programme',
    saved: 'Programme saved',
    savedDetail: 'The new settings apply to the next operation.',
    saveFailed: 'The programme could not be saved.',
    loadFailed: 'The programme could not be loaded.',
    channelRules: 'Channel rules',
    channelRulesHint: 'A multiplier applied on top of the rate for orders from a given channel — a double points day for delivery, say.',
    addRule: 'Add rule',
    channel: 'Channel',
    earnMultiplier: 'Earn ×',
    redeemMultiplier: 'Redeem ×',
    noRules: 'No channel rules',
    noRulesHint: 'Every channel earns and redeems at the standard rate.',
    addRuleTitle: 'Add a channel rule',
    editRuleTitle: 'Edit {channel}',
    channelHint: 'Matched against the channel sent with the order, e.g. DELIVERY.',
    earnMultiplierLabel: 'Earn multiplier',
    redeemMultiplierLabel: 'Redeem multiplier',
    ruleAdded: 'Channel rule added',
    ruleUpdated: 'Channel rule updated',
    ruleSaveFailed: 'The rule could not be saved.',
    removeRuleTitle: 'Remove this channel rule?',
    removeRuleMessage: 'Orders from {channel} will earn and redeem at the standard rate.',
    ruleRemoved: 'Channel rule removed',
    ruleRemoveFailed: 'Could not remove the rule',
    minutes: '{minutes} minutes',
    seconds: '{seconds} seconds',
    minutesSeconds: '{minutes} min {seconds} s',
  },

  notifications: {
    editSubtitle: 'Write the message the customer receives.',
    title: 'Notifications',
    subtitle: 'Which events text the customer, and what those texts say. Placeholders in braces are filled in when the message is sent.',
    alwaysOn: 'Always on',
    editMessage: 'Edit message',
    switchOn: 'Switch on',
    switchOff: 'Switch off',
    updatedAt: 'Updated {when}',
    messageLabel: 'Message',
    placeholdersHint: 'Placeholders available here — click to insert:',
    languageLabel: 'Language',
    languageHint: 'Arabic and other non-Latin text is sent as Unicode, which shortens each segment.',
    preview: 'Preview',
    previewUpdating: 'updating…',
    previewEmpty: 'Type a message to see it rendered.',
    previewCountOne: '{characters} characters · 1 SMS segment',
    previewCountMany: '{characters} characters · {segments} SMS segments',
    unresolvedOne: '{names} is not a placeholder this event provides, so it will be sent literally.',
    unresolvedMany: '{names} are not placeholders this event provides, so they will be sent literally.',
    saveMessage: 'Save message',
    switchedOn: 'Notification switched on',
    switchedOff: 'Notification switched off',
    toggleFailed: 'Could not change the notification',
    messageUpdated: 'Message updated',
    saveFailed: 'The message could not be saved.',
    loadFailed: 'The settings could not be loaded.',
    events: {
      REDEMPTION_OTP: 'Redemption code',
      POINTS_EARNED: 'Points earned',
      POINTS_REDEEMED: 'Points redeemed',
      POINTS_ADJUSTED: 'Points adjusted',
      POINTS_EXPIRING_SOON: 'Points expiring soon',
      POINTS_EXPIRED: 'Points expired',
      CUSTOMER_ENROLLED: 'Customer enrolled',
      SEGMENT_CHANGED: 'Segment changed',
      VOUCHER_ISSUED: 'Voucher issued',
      CASHBACK_AWARDED: 'Cashback awarded',
    },
    languages: {
      ENGLISH: 'English',
      ARABIC: 'Arabic',
      UNICODE: 'Unicode',
    },
    previewChars: '{count} characters',
    previewPartsOne: '{count} SMS segment',
    previewPartsMany: '{count} SMS segments',
  },

  sms: {
    updatedLabel: 'Updated',
    otpTemplateIdLabel: 'OTP template id',
    providersTitle: 'SMS gateways',
    providersSubtitle: 'One gateway is active at a time and sends every message — the redemption OTP included. Keys and passwords are stored write-only.',
    addGateway: 'Add gateway',
    noGateway: 'No gateway configured',
    noGatewayHint: 'Until one is active and enabled, no SMS is sent — which means no redemption can be confirmed.',
    activeBadge: 'Active',
    standbyBadge: 'Standby',
    sender: 'Sender',
    environment: 'Environment',
    environmentLive: 'Live',
    environmentTest: 'Test',
    nothingDelivered: '(nothing is delivered)',
    otpTemplate: 'OTP template',
    endpoint: 'Endpoint',
    credentials: 'Credentials',
    apiKeySet: 'API key set',
    passwordSet: 'Password set',
    tokenSet: 'Token set',
    sendTest: 'Send a test',
    makeActive: 'Make active',
    typeSmsMisr: 'SMS Misr',
    typeSmsMisrHint: 'The dedicated implementation, including the OTP endpoint.',
    typeGeneric: 'Generic HTTP',
    typeGenericHint: 'Any gateway with an HTTP API, configured below.',
    typeLogging: 'Log only',
    typeLoggingHint: 'Writes the message to the service log and sends nothing. For testing.',
    addTitle: 'Add a gateway',
    editTitle: 'Edit {name}',
    codeLabel: 'Code',
    codeHint: 'A short identifier, e.g. SMS_MISR_LIVE.',
    nameLabel: 'Name',
    typeLabel: 'Type',
    senderIdLabel: 'Sender ID',
    senderIdHint: 'The name the message appears from. Usually registered with the gateway.',
    environmentHint: 'Test accepts the request and delivers nothing.',
    otpTemplateHint: 'SMS Misr sends one-time codes through a pre-registered template.',
    username: 'Username',
    password: 'Password',
    passwordStored: 'A password is stored. Leave blank to keep it.',
    endpointUrl: 'Endpoint URL',
    method: 'Method',
    contentType: 'Content type',
    contentJson: 'JSON',
    contentForm: 'Form encoded',
    contentPlain: 'Plain',
    auth: 'Auth',
    authNone: 'None',
    authApiKey: 'API key header',
    authBasic: 'Basic',
    authBearer: 'Bearer',
    headerName: 'Header name',
    headerNameHint: 'For example X-API-Key.',
    apiKey: 'API key',
    apiKeyStored: 'A key is stored. Leave blank to keep it.',
    bearerToken: 'Bearer token',
    bearerTokenStored: 'A token is stored. Leave blank to keep it.',
    bodyTemplate: 'Body template',
    bodyTemplateHint: 'Use {tokens}. They are substituted per message.',
    successCodes: 'Success codes',
    successCodesHint: 'Comma-separated codes the gateway returns for an accepted message.',
    saveGateway: 'Add gateway',
    gatewayAdded: 'Gateway added',
    gatewayUpdated: 'Gateway updated',
    gatewaySaveFailed: 'The gateway could not be saved.',
    gatewayActivated: 'Gateway activated',
    gatewayActivatedDetail: '{name} now sends every message.',
    activateFailed: 'Could not activate the gateway',
    removeTitle: 'Remove this gateway?',
    removeMessage: '{name} will be deleted. Messages already sent through it keep their history.',
    gatewayRemoved: 'Gateway removed',
    removeFailed: 'Could not remove the gateway',
    testTitle: 'Send a test message',
    testPhoneHint: 'A real number — this sends.',
    testAccepted: 'The gateway accepted the message.',
    testRejected: 'The gateway rejected it.',
    testId: 'id {id}',
    sendTestButton: 'Send test',
    testFailed: 'The test could not be sent.',
    gatewaysFailed: 'The gateways could not be loaded.',

    logTitle: 'SMS log',
    logSubtitle: 'Every message the service queued and what the gateway said back. One-time codes are not recorded — only that one was sent.',
    queued: 'Queued',
    toColumn: 'To',
    kind: 'Kind',
    message: 'Message',
    tries: 'Tries',
    anyStatus: 'Any status',
    kindPlaceholder: 'e.g. REDEMPTION_OTP',
    failedWarningOne: 'One message on this page failed. Check the active gateway’s credentials and balance before re-queuing it.',
    failedWarningMany: '{count} messages on this page failed. Check the active gateway’s credentials and balance before re-queuing them.',
    logEmpty: 'No messages in this range',
    logEmptyHint: 'Widen the dates, or clear the status and phone filters.',
    messageTitle: 'Message',
    attempts: 'Attempts',
    gateway: 'Gateway',
    gatewayId: 'Gateway id',
    linkedTo: 'Linked to',
    scheduled: 'Scheduled',
    processed: 'Processed',
    requeued: 'Message re-queued',
    requeuedDetail: 'To {phone}.',
    requeueFailed: 'Could not re-queue the message',
    logFailed: 'The log could not be loaded.',
  },

  users: {
    searchLabel: 'Search users',
    title: 'Users',
    subtitle: 'Keycloak proves who someone is; this list decides what they may do. Someone who signs in but is not here has no access.',
    invite: 'Invite user',
    searchPlaceholder: 'Name, username or email',
    username: 'Username',
    roles: 'Roles',
    lastSignedIn: 'Last signed in',
    you: 'You',
    noRole: 'No role — cannot do anything',
    suspend: 'Suspend',
    reinstate: 'Reinstate',
    emptyTitle: 'No users match',
    emptyHint: 'Invite someone, or clear the filters.',
    inviteTitle: 'Invite a user',
    editTitle: 'Edit {name}',
    inviteSubtitle: 'The username must match their Keycloak account, or they will not be recognised when they sign in.',
    usernameHint: 'As it appears in Keycloak.',
    displayName: 'Display name',
    rolesHint: 'Permissions come from roles. Someone with no role can sign in and see nothing.',
    noRoleWarning: 'Without a role this user will be able to sign in but do nothing.',
    inviteButton: 'Invite',
    invited: 'User invited',
    invitedDetail: '{name} can sign in once they have a Keycloak account for {username}.',
    updated: 'User updated',
    saveFailed: 'The user could not be saved.',
    suspended: 'User suspended',
    reinstated: 'User reinstated',
    statusFailed: 'Could not change the user',
    loadFailed: 'The users could not be loaded.',
  },

  roles: {
    title: 'Roles',
    subtitle: 'A role is a set of permissions. The service checks the permission, not the role, and the same check applies to every caller.',
    create: 'New role',
    builtIn: 'Built in',
    permissionCountOne: '{held} of {total} permissions · 1 user',
    permissionCountMany: '{held} of {total} permissions · {users} users',
    andMore: '+{count} more',
    view: 'View',
    createTitle: 'New role',
    modalSubtitle: 'Tick what this role may do. Everything not ticked is refused by the service.',
    codeLabel: 'Code',
    codeHint: 'Uppercase, e.g. CALL_CENTRE_AGENT.',
    descriptionPlaceholder: 'What this role is for',
    systemRoleWarning: 'This is a built-in role. It is shown here for reference and cannot be changed — create a role of your own to grant a different set.',
    created: 'Role created',
    updated: 'Role updated',
    saveFailed: 'The role could not be saved.',
    deleteTitle: 'Delete this role?',
    deleteHeldOne: '{name} is held by 1 user. They lose every permission this role gave them.',
    deleteHeldMany: '{name} is held by {count} users. They lose every permission this role gave them.',
    deleteUnheld: '{name} will be deleted. No user holds it.',
    deleted: 'Role deleted',
    deleteFailed: 'Could not delete the role',
    loadFailed: 'The roles could not be loaded.',
    permissionCount: '{held} of {total} permissions',
    userCountOne: '{count} user',
    userCountMany: '{count} users',
    morePermissions: '+{count} more',
  },

  vouchers: {
    title: 'Vouchers',
    subtitle: 'A code is worth money and can be spent once. New vouchers start switched off — activate one deliberately before it can issue.',
    create: 'New voucher',
    countVouchers: 'Vouchers',
    countActive: 'Active',
    codesOutstanding: 'Codes outstanding',
    codesOutstandingHint: "Available or in a customer's hands",
    codesRedeemed: 'Codes redeemed',
    kind: 'Kind',
    worth: 'Worth',
    codes: 'Codes',
    window: 'Window',
    free: '{count} free',
    held: '{count} held',
    spent: '{count} spent',
    mintsOnDemand: 'Mints on demand',
    always: 'Always',
    fromDate: 'From {date}',
    untilDate: 'Until {date}',
    dateRange: '{from} – {to}',
    emptyTitle: 'No vouchers yet',
    emptyHint: 'Create one, then generate codes or let it mint them on demand.',
    codesButton: 'Codes',
    switchOff: 'Switch off',
    activate: 'Activate',
    createTitle: 'New voucher',
    editTitle: 'Edit {name}',
    categoryLabel: 'Kind',
    categoryHint: 'Cashback vouchers are created by the offers themselves.',
    categoryStandard: 'Standard',
    categoryCampaign: 'Campaign',
    categoryCashback: 'Cashback',
    valueTypeLabel: 'Value type',
    valueTypeFixed: 'Fixed amount',
    valueTypePercentage: 'Percentage of the order',
    valuePercent: 'Value %',
    valueLabel: 'Value',
    valuePercentHint: 'Of the order total, capped at the whole order.',
    valueFixedHint: 'Never more than the order comes to.',
    codeExpiryDays: 'Codes expire after (days)',
    codeExpiryHint: 'Blank means a code lasts as long as the voucher does.',
    starts: 'Starts',
    ends: 'Ends',
    autoStock: 'Mint codes on demand',
    autoStockHint: 'Off means codes come from a pool you generate in advance, and the voucher goes out of stock when that pool empties.',
    createButton: 'Create voucher',
    created: 'Voucher created',
    createdDetail: '{name} is switched off until you activate it.',
    updated: 'Voucher updated',
    saveFailed: 'The voucher could not be saved.',
    activated: 'Voucher activated',
    switchedOff: 'Voucher switched off',
    toggleFailed: 'Could not change the voucher',
    generateTitle: 'Generate codes',
    howMany: 'How many',
    generateExpiry: 'Expire after (days)',
    generateExpiryHint: "Overrides the voucher's own setting for this batch.",
    generated: '{count} codes generated',
    generatedDetail: '{count} codes added to the pool.',
    generateFailed: 'The codes could not be generated.',
    generateButton: 'Generate',
    loadFailed: 'The vouchers could not be loaded.',
    stockFree: '{count} free',
    stockHeld: '{count} held',
    stockSpent: '{count} spent',
    codesAdded: '{count} codes added to the pool.',
  },

  voucherCodes: {
    codeLabel: 'Voucher code',
    title: 'Voucher codes',
    subtitle: 'Find a code, see who holds it, and spend it against an order. A code can be redeemed once.',
    findTitle: 'Find a code',
    lookUp: 'Look up',
    notFound: 'No voucher has that code.',
    lookupFailed: 'The lookup failed.',
    voucher: 'Voucher',
    worth: 'Worth',
    holder: 'Holder',
    issuedBy: 'Issued by',
    issued: 'Issued',
    redeemedAt: 'Redeemed',
    codesTitle: 'Codes',
    emptyTitle: 'No codes',
    emptyHint: 'Generate a batch, or let the voucher mint them as they are issued.',
    redeem: 'Redeem',
    withdraw: 'Withdraw',
    redeemTitle: 'Redeem a voucher code',
    redeemIntro: 'Find the customer this code is being used for. The service refuses a code that was issued to somebody else.',
    orderTotalRequired: 'Required: this voucher is a share of the order.',
    orderTotalOptional: 'Optional. The discount is capped at what the order came to.',
    redeemed: '{amount} off',
    redeemedDetail: 'Voucher {code} redeemed for {customer}.',
    redeemResult: '{amount} off this order. Recorded on the ledger as entry {id}.',
    redeemFailed: 'The code could not be redeemed.',
    withdrawTitle: 'Withdraw this code?',
    withdrawMessage: '{code} can no longer be redeemed. A code already spent cannot be withdrawn — raise a refund against the ledger entry instead.',
    withdrawn: 'Code withdrawn',
    withdrawFailed: 'Could not withdraw the code',
    loadFailed: 'The codes could not be loaded.',
    redeemLookupHint: 'Find the customer this code is being used for. The service refuses a code that was issued to somebody else.',
  },

  cashback: {
    title: 'Cashback',
    subtitle: 'An order that qualifies issues the customer a voucher automatically, without anybody at the counter doing anything.',
    create: 'New offer',
    offer: 'Offer',
    givesBack: 'Gives back',
    onOrders: 'On orders',
    segments: 'Segments',
    perCustomer: 'Per customer',
    window: 'Window',
    everyone: 'Everyone',
    noLimit: 'No limit',
    times: '{count}×',
    anyOrder: 'Any order',
    andUp: '{amount} and up',
    upTo: 'Up to {amount}',
    range: '{min} – {max}',
    always: 'Always',
    fromDate: 'From {date}',
    untilDate: 'Until {date}',
    dateRange: '{from} – {to}',
    emptyTitle: 'No cashback offers',
    emptyHint: 'Create one to give something back on qualifying orders.',
    pause: 'Pause',
    run: 'Run',
    createTitle: 'New cashback offer',
    editTitle: 'Edit {name}',
    rewardType: 'Reward type',
    rewardPercentage: 'Percentage of the order',
    rewardFixed: 'Fixed amount',
    givesBackPercent: 'Gives back %',
    givesBackLabel: 'Gives back',
    rewardHint: 'Issued to the customer as a voucher code.',
    minimumOrder: 'Minimum order',
    minimumOrderHint: 'Below this, nothing is given back.',
    maximumOrder: 'Maximum order',
    maximumOrderHint: 'Blank means no upper bound.',
    timesPerCustomer: 'Times per customer',
    timesPerCustomerHint: 'Blank means as often as they qualify.',
    unbounded: 'This offer has no maximum order and no per-customer limit, so a single large order gives back a proportionally large voucher, as often as it happens.',
    starts: 'Starts',
    ends: 'Ends',
    voucherExpiry: 'Voucher expires after (days)',
    segmentsHint: 'Leave all unticked to offer this to every customer.',
    createButton: 'Create offer',
    created: 'Offer created',
    updated: 'Offer updated',
    saveFailed: 'The offer could not be saved.',
    paused: 'Offer paused',
    running: 'Offer running',
    toggleFailed: 'Could not change the offer',
    loadFailed: 'The offers could not be loaded.',
    unboundedWarning: 'This offer has no maximum order and no per-customer limit, so a single large order gives back a proportionally large voucher, as often as it happens.',
  },

  campaigns: {
    title: 'Campaigns',
    subtitle: 'Build an audience from what customers have done, look at it, then export it, text it, or give every member a voucher.',
    audience: 'Audience',
    segmentsHint: 'None ticked means every segment.',
    anyStatus: 'Any status',
    pointsFrom: 'Points from',
    pointsTo: 'Points to',
    spendFrom: 'Spend from',
    spendTo: 'Spend to',
    quietFor: 'Quiet for at least (days)',
    quietForHint: 'Customers with no points movement for this long. Never having transacted counts, measured from enrolment.',
    birthdayMonth: 'Birthday month',
    anyMonth: 'Any month',
    sex: 'Sex',
    sexMale: 'Male',
    sexFemale: 'Female',
    sexOther: 'Other',
    reachAtMost: 'Reach at most',
    reachAtMostHint: 'Caps the execution. The biggest balances are kept when the audience is trimmed.',
    preview: 'Preview audience',
    audienceCount: 'Audience',
    pointsHeld: 'Points held',
    pointsHeldHint: 'Across the whole audience',
    lifetimeSpend: 'Lifetime spend',
    whoThisReaches: 'Who this reaches',
    sendSms: 'Send SMS',
    issueVouchers: 'Issue vouchers',
    noActiveVoucher: 'No active voucher to issue. Activate one first.',
    emptyTitle: 'Nobody matches',
    emptyHint: 'Loosen the filters — a campaign to nobody is usually a filter that is too tight.',
    noAudienceTitle: 'No audience yet',
    noAudienceHint: 'Set the filters and preview. Nothing can be sent until you have seen who it would reach.',
    lastActivity: 'Last activity',
    buildFailed: 'The audience could not be built.',
    exportFailed: 'The export failed',
    smsTitle: 'Text this audience',
    customerCount: '{count} customers',
    campaignName: 'Campaign name',
    campaignNameHint: 'Recorded against every message sent.',
    untitled: 'Untitled campaign',
    messageLabel: 'Message',
    smsTemplateHint: 'Use {tokens}. Anything else is sent as written.',
    smsWarning: 'This queues a message to {count} customers and cannot be recalled once it starts sending.',
    smsResult: '{queued} queued of {total}.',
    skipped: '{count} skipped — {reason}',
    queueMessages: 'Queue {count} messages',
    messagesQueued: '{count} messages queued',
    smsFailed: 'The campaign could not be queued.',
    voucherTitle: 'Issue a voucher to this audience',
    voucherLabel: 'Voucher',
    textEachCustomer: 'Text each customer their code',
    voucherTemplateHint: 'Use {tokens}.',
    voucherWarning: 'This issues a code to each of {count} customers. Each code is worth money and cannot be un-issued — only withdrawn one at a time.',
    voucherResult: '{issued} codes issued, {queued} messages queued.',
    issueCodes: 'Issue {count} codes',
    codesIssued: '{count} codes issued',
    voucherFailed: 'The vouchers could not be issued.',
  },

  counter: {
    title: 'Counter',
    subtitle: 'Find the customer, ring up the order, then key the discount shown here into the POS.',
    callCentre: 'Call centre',
    posNumber: 'POS order number',
    unassignedTitle: 'You are not assigned to a branch yet.',
    unassignedMessage: 'Ask an administrator to assign you to your branch under Counter settings, then reload this page.',
    identify: {
      title: 'Identify the customer',
      appCode: 'App code',
      phone: 'Phone (no app)',
      codeLabel: 'The code from the customer’s app',
      codeHint: 'Scan the QR code, or type the code the customer reads out. Spaces do not matter.',
      phoneHint: 'For a customer without the app. Earning needs nothing more; spending texts them a code.',
      find: 'Find',
    },
    order: {
      title: 'The order',
      changeCustomer: 'Different customer',
      posNumberHint: 'Exactly as the POS printed it. One order can only be recorded once.',
      orderTotalHint: 'The total before any points discount.',
      usePoints: 'Use points',
      cannotSpend: 'Points cannot be spent on this order.',
      otpNotice: 'A code will be texted to {phone}. The customer reads it back to approve the discount.',
      approvalNotice: 'The customer will be asked to approve this discount in the Maky app before anything is spent.',
      discount: 'Discount',
      discountRange: 'Between {min} and {max} on this order.',
      discountOutOfRange: 'The discount must be between {min} and {max}.',
      summary: 'Summary',
      enterTotal: 'Enter the order total to see what the customer can spend and earn.',
      discountToKey: 'Discount to key into the POS',
      netPayable: 'Customer pays',
      pointsSpent: 'Points spent',
      estimatedEarned: 'Points to be earned (estimate)',
      needsPosNumber: 'Enter the POS order number to continue.',
      record: 'Confirm',
      textCode: 'Text a code',
      askApproval: 'Ask for approval in the app',
      safeToRetry: 'Press Confirm again: the order will not be recorded twice.',
      memberDiscount: 'Member discount',
      memberDiscountOf: 'Member discount ({detail})',
    },
    result: {
      enterDiscount: 'Enter this discount in the POS',
      noDiscount: 'No discount — points earned only',
      earned: '+{points} points earned',
      newBalance: 'New balance',
      nextOrder: 'Next order',
      startAgain: 'Start again',
      waitingApproval: 'Waiting for the customer to approve in the Maky app',
      waitingDetail: 'They are being asked to approve a discount of {discount}. This updates by itself.',
      cancelFailed: 'Could not cancel',
      nothingMoved: 'Nothing was spent or earned. Do not give a discount on this order.',
      declined: 'The customer declined',
      cancelled: 'Cancelled',
      expired: 'The time to approve ran out',
      failed: 'The order could not be recorded',
    },
    otp: {
      sent: 'A code was texted to {phone}. Ask the customer to read it out.',
      label: 'Code from the text message',
      expiresIn: 'Expires in {time}',
      expired: 'The code has expired.',
      resendsLeft: '{count} resends left',
      resend: 'Resend the code',
      resent: 'Code sent again',
    },
    today: {
      title: 'Today at this branch',
      time: 'Time',
      earned: 'Points earned',
      emptyTitle: 'No orders yet today',
      emptyHint: 'Orders recorded here appear in this list.',
    },
    reversal: {
      request: 'Request reversal',
      title: 'Request a reversal',
      explain: 'A manager must approve this in Refunds. Nothing changes until then: the points spent are returned and the points earned are taken back together, or not at all.',
      reasonHint: 'Why the order is being reversed. The manager reads this.',
      raised: 'Reversal requested',
      raisedDetail: 'It is waiting for a manager to approve it.',
      status: {
        PENDING_APPROVAL: 'Reversal pending',
        APPROVED: 'Reversed',
        REJECTED: 'Reversal rejected',
        CANCELLED: 'Reversal withdrawn',
      },
    },
    errors: {
      codeLocked: 'Too many wrong codes. Code lookups are locked for ten minutes — use the phone tab or wait.',
      codeInvalid: 'That code is wrong, expired or already used. Ask the customer to show a fresh one from the app.',
      methodNotAllowed: 'That is not how this order can be recorded: the customer must approve it another way. Check the order and confirm again.',
      branchInactive: 'Your branch is switched off. Ask an admin.',
      entryNotFound: 'That order could not be found.',
      entryNotPending: 'That order is no longer waiting — it was already completed, declined or cancelled.',
      entryExpired: 'That order timed out. Start it again.',
      nothingToReverse: 'This order moved no points, so there is nothing to reverse.',
      orderAlreadyRecorded: 'This POS order number has already been recorded at this branch.',
      staffUnassigned: 'You are not assigned to a branch. Ask an administrator to assign you one.',
      customerNotFound: 'No customer is enrolled with that number.',
      customerNotActive: 'This customer’s account is not active.',
      insufficientPoints: 'The customer does not have enough points for that discount.',
      discountBelowMinimum: 'That discount is below the minimum allowed on this order.',
      discountAboveMaximum: 'That discount is above the maximum allowed on this order.',
      otpInvalid: 'That code is not correct. Ask the customer to read it again.',
      otpExpired: 'The texted code has expired.',
      otpLocked: 'Too many wrong codes. This order can no longer spend points — start again.',
      otpResendLimit: 'The code cannot be sent again.',
      network: 'The service could not be reached. Check the connection.',
      forbidden: 'You do not have permission to do that.',
      generic: 'Something went wrong. Try again, or ask a supervisor.',
    },
    settings: {
      title: 'Counter settings',
      subtitle: 'Branches, and which branch each member of staff records orders at.',
      branches: 'Branches',
      addBranch: 'Add branch',
      editBranch: 'Edit branch',
      nameEn: 'Name (English)',
      nameAr: 'Name (Arabic)',
      kindBranch: 'Branch',
      kindHint: 'At a call centre, spending points waits for the customer to approve in the app.',
      codeHint: 'Short and permanent, e.g. ARENA. Recorded on every ledger entry from this branch.',
      codeRule: '2 to 32 capital letters, digits or underscores.',
      branchCreated: 'Branch added',
      branchUpdated: 'Branch updated',
      saveFailed: 'The branch could not be saved.',
      loadFailed: 'The counter settings could not be loaded.',
      approval: 'Customer approval',
      requireAppApproval: 'Require app approval',
      requireAppApprovalHint: 'Any order that spends points or gives a member discount waits for the customer to approve it in the Maky app — at every branch, not only the call centre. A customer without the app is texted a code instead. Off, a branch spends on the app code alone and the member discount is given without asking.',
      approvalSaved: 'Approval setting saved',
      approvalSaveFailed: 'The approval setting could not be saved',
      noBranches: 'No branches yet',
      noBranchesHint: 'Add the branches and call centre that take orders.',
      staff: 'Staff',
      staffHint: 'Orders are recorded against the branch assigned here, never one the cashier picks.',
      roles: 'Roles',
      branch: 'Branch',
      branchFor: 'Branch for {name}',
      noBranch: 'Not assigned',
      noStaff: 'No staff',
      noStaffHint: 'Users with counter access appear here.',
      addBranchFirst: 'Add an active branch before assigning staff.',
      assigned: 'Assignment saved',
      unassignedDetail: '{name} is no longer assigned to a branch.',
      assignFailed: 'The assignment could not be saved',
    },
    report: {
      title: 'Counter report',
      subtitle: 'One day at the counter, to reconcile against the POS end-of-day report.',
      allBranches: 'All branches',
      method: 'Method',
      methods: {
        APP_CODE: 'App code',
        PHONE: 'Phone',
        SMS_OTP: 'Phone + SMS code',
      },
      flags: 'Flags',
      flag: {
        PHONE_REPEATED: 'Phone repeated',
        REFERENCE_REUSED_AFTER_REVERSAL: 'POS number reused after reversal',
      },
      reversal: 'Reversal',
      cashier: 'Cashier',
      entries: 'Orders',
      discountGiven: 'Discount given',
      pointsSpentHint: '{points} points spent',
      exported: 'Report exported',
      emptyTitle: 'No orders on this day',
      emptyHint: 'Pick another date or branch.',
    },
  },

  memberships: {
    title: 'Membership applications',
    subtitle: 'Customers applying from the app to join a membership segment with their membership number and card. Approval moves them into the segment straight away.',
    submitted: 'Submitted',
    membershipNumber: 'Membership number',
    allSegments: 'All segments',
    emptyTitle: 'No applications here',
    emptyHint: 'Applications sent from the app appear here for review.',
    review: 'Review',
    view: 'View',
    detailTitle: 'Membership application',
    duplicatesTitle: 'Possible duplicate: this membership number is used elsewhere in this segment ({count})',
    duplicatesHint: 'Check the card carefully. The same number on another account may mean a shared or borrowed card.',
    applicationRef: 'application #{id}',
    reviewed: 'Reviewed',
    rejectReason: 'Rejection reason',
    revoked: 'Revoked',
    revokeReason: 'Revocation reason',
    cardFront: 'Card — front',
    cardBack: 'Card — back',
    zoom: 'View full size',
    cardLogged: 'Card photos are decrypted only for you to view, and every view is logged.',
    readOnly: 'You can view this application but not decide it.',
    approve: 'Approve',
    reject: 'Reject',
    approveTitle: 'Approve this membership?',
    approveMessage: '{customer} will be moved into {segment} now and notified. Their member discount applies from their next order.',
    approveDespiteDuplicates: 'This membership number is already used elsewhere in this segment ({count}). Approve only if you are sure the card is theirs.',
    rejectTitle: 'Reject this application',
    rejectHint: 'Sent to the customer in the app, so write it for them.',
    approved: 'Membership approved',
    approvedDetail: 'The customer is now in {segment}.',
    rejected: 'Application rejected',
    reasonRequired: 'Give a reason of at least a few characters.',
    card: {
      noBack: 'The customer did not send the back of the card.',
      shredded: 'This card photo has been permanently deleted and can no longer be shown.',
      notConfigured: 'Card photos cannot be shown: the decryption key is not configured on the service.',
      missing: 'This card photo is not available.',
      failed: 'The card photo could not be loaded.',
    },
    errors: {
      notFound: 'That application no longer exists.',
      notPending: 'This application has already been decided or withdrawn. Refresh to see where it stands.',
      alreadyMember: 'The customer is already a member of a segment.',
      notMember: 'The customer is not a member through an approved application, so there is nothing to revoke.',
      segmentInactive: 'That segment is no longer active.',
      customerNotActive: 'The customer is not active.',
      customerNotFound: 'That customer does not exist.',
      reasonRequired: 'A reason is required.',
      generic: 'That could not be done. Try again, or refresh the page.',
    },
  },

  noAccess: {
    title: 'No access',
    heading: 'You do not have access to anything yet',
    message: 'Your account exists, but no role has been assigned to it, so there is nothing here you can open. An administrator can assign one under Users.',
    signedInAs: 'Signed in as {username} ({email})',
  },

  notFound: {
    title: 'Not found',
    heading: 'That page does not exist',
    message: 'The link may be out of date, or the page may have moved.',
    goHome: 'Back to the dashboard',
  },
} as const
